import React, { useCallback, useRef, useState } from "react"
import { Check, Copy, Loader2 } from "lucide-react"
import { ErrorIcon } from "../icons"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { useMeContext } from "@/model/user/provider"
import { createLogger } from "@/model/logger"
import { renderMessageContent } from "../utils/messageHelpers"
import { ChatProcessingStatus } from "@/graphql/main/gql/graphql"
import { useCopyToClipboard, useDevice } from "@shared/hooks"
import type { MessageStatusData, UIText } from "../types"
import { statusErrorText } from "../utils/statusErrors"
import { useLongPress } from "@/features/chat/hooks/useLongPress"
import { BackArrowIcon } from "../icons"
import { TRAININGS_PAGE } from "@shared/constants/navigation"

const logger = createLogger("ChatMessageList")

interface Message {
  id?: string | null
  text?: string | null
  role?: string | null
  mentionedAthleteIDs?: readonly string[] | null
  createdAt?: string | null
}

interface ChatMessageListProps {
  messages: Message[]
  conversationsLoading: boolean
  selectedConversation: string | null
  pendingMessage: string | null
  isSendingMessage: boolean
  pollingMessageId: string | null
  messageStatus: MessageStatusData | undefined
  isPollingComplete: boolean
  onCancelSendMessage: () => void
  onQuestionClick: (question: string) => void
  selectedCategory: string | null
  setSelectedCategory: (category: string | null) => void
  randomQuestions: string[]
  setRandomQuestions: (questions: string[]) => void
  getRandomQuestions: (count: number, category?: string) => string[]
  getFilteredCategories: () => Record<string, { title: string; questions: string[] }>
  uiText: UIText
  currentLang: string
  messagesEndRef: React.RefObject<HTMLDivElement | null>
}

const ChatMessageListComponent: React.FC<ChatMessageListProps> = ({
  messages,
  conversationsLoading,
  selectedConversation,
  pendingMessage,
  isSendingMessage,
  pollingMessageId,
  messageStatus,
  isPollingComplete,
  onCancelSendMessage,
  onQuestionClick,
  selectedCategory,
  setSelectedCategory,
  randomQuestions,
  setRandomQuestions,
  getRandomQuestions,
  getFilteredCategories,
  uiText,
  messagesEndRef,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { me } = useMeContext()
  const { isMobile } = useDevice()
  const { copyText } = useCopyToClipboard()

  const [isHoveringMessage, setIsHoveringMessage] = useState<string | null>(null)
  const [longPressMessageId, setLongPressMessageId] = useState<string | null>(null)
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null)

  // Create a single long press handler that can be configured per message
  const currentMessageIdRef = useRef<string>("")

  const { handlers: longPressHandlers } = useLongPress({
    onLongPress: () => {
      if (currentMessageIdRef.current) {
        setLongPressMessageId(currentMessageIdRef.current)
      }
    },
  })

  // Get long press handlers for a specific message
  const getLongPressHandlers = useCallback(
    (messageId: string) => {
      if (!isMobile) return {}

      // Update the ref to track which message is being pressed
      return {
        ...longPressHandlers,
        onTouchStart: () => {
          currentMessageIdRef.current = messageId
          longPressHandlers.onTouchStart()
        },
        onMouseDown: () => {
          currentMessageIdRef.current = messageId
          longPressHandlers.onMouseDown()
        },
      }
    },
    [isMobile, longPressHandlers],
  )

  const handleCopyMessage = async (text: string, messageId: string) => {
    const success = await copyText(text, { showSuccessNotification: false, showErrorNotification: false })
    if (success) {
      logger.debug("Message copied to clipboard", { messageId })
      setCopiedMessageId(messageId)
      setTimeout(() => setCopiedMessageId(null), 2000)
    } else {
      logger.warn("Failed to copy message to clipboard", { messageId })
    }
  }

  const handleAthleteClick = (athleteIds: readonly string[]) => {
    // Navigate to trainings page with athlete IDs
    navigate(TRAININGS_PAGE, {
      state: {
        chatMentionedAthleteIDs: [...athleteIds],
      },
    })
  }

  if (conversationsLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader2
          className="animate-spin"
          size={24}
        />
      </div>
    )
  }

  if (!selectedConversation || (messages.length === 0 && !isSendingMessage)) {
    // The start screen is the first thing in the thread, so it flows from the
    // top of the scroller in the same 12px rhythm as the messages — not centred
    // in the panel. The disclaimer is said once, in the header, and does not
    // repeat here as a yellow plate (docs/design/AI Assistant.dc.html).
    return (
      <div className="flex flex-col gap-3">
        <div className="mb-1 flex flex-col gap-1">
          <h3 className="text-xl font-semibold tracking-[-0.01em] max-md:text-[17px]">
            {selectedConversation ? uiText.newChatCreated : uiText.welcome}
          </h3>
          <p className="text-sm text-muted-foreground">
            {selectedConversation ? uiText.askOrChoose : uiText.selectOrStart}
          </p>
        </div>

        {!selectedCategory ? (
          <>
            <p className="text-sm font-medium">{uiText.selectCategory}</p>
            <div className="grid grid-cols-3 gap-2 max-md:grid-cols-2">
              {Object.entries(getFilteredCategories()).map(([key, category]) => (
                <button
                  key={key}
                  onClick={() => {
                    setSelectedCategory(key)
                    setRandomQuestions(getRandomQuestions(5, key))
                  }}
                  className="flex min-h-16 cursor-pointer items-center justify-center rounded-lg border border-border bg-white px-3.5 py-3 text-center text-sm font-semibold text-pretty transition-colors hover:border-primary hover:bg-blue-tint max-md:min-h-14"
                >
                  {category.title}
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <button
              onClick={() => {
                setSelectedCategory(null)
                setRandomQuestions(getRandomQuestions(5))
              }}
              className="flex min-h-9 cursor-pointer items-center gap-2 self-start rounded-lg bg-blue-50 px-3 text-sm font-medium text-primary transition-colors hover:bg-blue-100 max-md:min-h-11"
            >
              <BackArrowIcon className="w-4 h-4" />
              {uiText.backToCategories}
            </button>
            <div className="flex flex-col gap-2">
              {randomQuestions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => onQuestionClick(question)}
                  disabled={isSendingMessage}
                  className="flex min-h-12 w-full cursor-pointer items-center gap-2.5 rounded-lg border border-border bg-white px-3.5 py-2.5 text-left text-sm text-pretty transition-colors hover:border-primary hover:bg-blue-tint disabled:cursor-not-allowed disabled:opacity-60 max-md:min-h-13"
                >
                  <span className="flex-none text-gray-400">→</span>
                  {question}
                </button>
              ))}
            </div>
            <button
              onClick={() => setRandomQuestions(getRandomQuestions(5, selectedCategory))}
              className="min-h-9 cursor-pointer self-start rounded-lg px-3 text-sm font-medium text-primary transition-colors hover:bg-blue-50 max-md:min-h-11 max-md:self-stretch"
            >
              {uiText.moreQuestions}
            </button>
          </>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Screen reader announcement for new messages */}
      <div
        className="sr-only"
        role="log"
        aria-live="polite"
        aria-label="Chat messages"
      >
        {messages[messages.length - 1]?.role === "ASSISTANT" && (
          <div>Assistant says: {messages[messages.length - 1]?.text}</div>
        )}
      </div>

      {messages.map((message) => (
        <div
          key={message.id}
          className={`group relative text-sm leading-relaxed transition-colors ${
            message.role === "USER"
              ? "self-end ml-auto max-w-[78%] rounded-xl border border-blue-100 bg-blue-50 p-3.5 max-md:max-w-[88%]"
              : "self-start w-full rounded-xl border border-border bg-white p-3.5"
          }`}
          onMouseEnter={() => setIsHoveringMessage(message.id || null)}
          onMouseLeave={() => setIsHoveringMessage(null)}
          {...getLongPressHandlers(message.id || "")}
          role="article"
          aria-label={`${message.role === "USER" ? "Your message" : "Assistant message"}: ${message.text?.substring(0, 100)}...`}
        >
          <div className="prose prose-sm dark:prose-invert max-w-none">
            {renderMessageContent(message.text || "", message.mentionedAthleteIDs, me.isCoach, handleAthleteClick, t)}
          </div>
          {/* Copy button for desktop (hover) and mobile (long press) */}
          {((!isMobile && isHoveringMessage === message.id) ||
            (isMobile && longPressMessageId === message.id) ||
            copiedMessageId === message.id) && (
            <button
              onClick={() => {
                if (copiedMessageId !== message.id) {
                  // copyText catches its own failures and reports them as `false`
                  void handleCopyMessage(message.text || "", message.id || "")
                  setLongPressMessageId(null)
                }
              }}
              className={`absolute top-2 right-2 p-1.5 rounded-lg shadow-sm border transition-all duration-200 ${
                copiedMessageId === message.id
                  ? "bg-green-500 border-green-500 text-white"
                  : "bg-white/80 hover:bg-white border-gray-200 text-gray-600 hover:text-gray-800"
              }`}
              title={copiedMessageId === message.id ? t("chat.copied") : t("chat.copy")}
            >
              {copiedMessageId === message.id ? <Check size={16} /> : <Copy size={16} />}
            </button>
          )}
          {/* Copy success message */}
          {copiedMessageId === message.id && (
            <div className="absolute top-2 right-12 px-2 py-1 bg-green-500 text-white text-xs rounded-lg shadow-sm animate-pulse">
              {t("chat.copied")}
            </div>
          )}
        </div>
      ))}

      {pendingMessage && (
        <div className="self-end ml-auto max-w-[78%] rounded-xl border border-blue-100 bg-blue-50 p-3.5 max-md:max-w-[88%] text-sm leading-relaxed opacity-70">
          <div className="prose prose-sm dark:prose-invert max-w-none">{pendingMessage}</div>
        </div>
      )}

      <div ref={messagesEndRef}>
        {(isSendingMessage || pollingMessageId) && !isPollingComplete && (
          <div className="self-start w-full rounded-xl border border-border bg-white p-3.5 text-sm leading-relaxed">
            {/* Screen reader announcement for status updates */}
            <div
              className="sr-only"
              role="status"
              aria-live="polite"
            >
              {messageStatus?.chatMessageStatus?.status === ChatProcessingStatus.Received && t("chat.messageReceived")}
              {messageStatus?.chatMessageStatus?.status === ChatProcessingStatus.Thinking && t("chat.thinking")}
              {messageStatus?.chatMessageStatus?.status === ChatProcessingStatus.CallingFunction &&
                `${t("chat.callingFunction")}: ${messageStatus.chatMessageStatus.currentFunction}`}
              {messageStatus?.chatMessageStatus?.status === ChatProcessingStatus.ProcessingResults &&
                t("chat.processingResults")}
              {(messageStatus?.chatMessageStatus?.status as ChatProcessingStatus) === ChatProcessingStatus.Error &&
                statusErrorText(messageStatus?.chatMessageStatus?.error, t)}
            </div>

            <div className="flex flex-col space-y-3">
              {/* Status display */}
              <div className="flex items-center space-x-2">
                {messageStatus?.chatMessageStatus?.status === ChatProcessingStatus.Received && (
                  <>
                    <Loader2
                      className="animate-spin text-gray-400"
                      size={16}
                    />
                    <span className="text-sm text-gray-600">{t("chat.messageReceived")}</span>
                  </>
                )}
                {messageStatus?.chatMessageStatus?.status === ChatProcessingStatus.Thinking && (
                  <>
                    <Loader2
                      className="animate-spin text-gray-400"
                      size={16}
                    />
                    <span className="text-sm text-gray-600">
                      {messageStatus.chatMessageStatus.statusMessage || t("chat.thinking")}
                    </span>
                  </>
                )}
                {messageStatus?.chatMessageStatus?.status === ChatProcessingStatus.CallingFunction && (
                  <>
                    <Loader2
                      className="animate-spin text-gray-400"
                      size={16}
                    />
                    <span className="text-sm text-gray-600">
                      {t("chat.callingFunction")}:
                      <span className="font-medium">{messageStatus.chatMessageStatus.currentFunction}</span>
                    </span>
                  </>
                )}
                {messageStatus?.chatMessageStatus?.status === ChatProcessingStatus.ProcessingResults && (
                  <>
                    <Loader2
                      className="animate-spin text-gray-400"
                      size={16}
                    />
                    <span className="text-sm text-gray-600">{t("chat.processingResults")}</span>
                  </>
                )}
                {(messageStatus?.chatMessageStatus?.status as ChatProcessingStatus) === ChatProcessingStatus.Error && (
                  <>
                    <ErrorIcon
                      className="flex-none text-danger-strong"
                      size={16}
                    />
                    <span className="text-sm text-danger-strong">
                      {statusErrorText(messageStatus?.chatMessageStatus?.error, t)}
                    </span>
                  </>
                )}
                {!messageStatus?.chatMessageStatus?.status && (
                  <>
                    <Loader2
                      className="animate-spin text-gray-400"
                      size={16}
                    />
                    <span className="text-sm text-gray-600">{t("chat.sendingMessage")}</span>
                  </>
                )}
              </div>

              {/* Progress bar */}
              {messageStatus?.chatMessageStatus?.progress !== undefined && (
                <div className="h-1 w-full overflow-hidden rounded-full bg-border">
                  <div
                    className="h-full bg-primary transition-all duration-300"
                    style={{ width: `${messageStatus.chatMessageStatus.progress}%` }}
                  />
                </div>
              )}

              {/* Function calls list */}
              {messageStatus?.chatMessageStatus?.functionCalls &&
                messageStatus.chatMessageStatus.functionCalls.length > 0 && (
                  <div className="text-xs text-gray-500">
                    <div className="font-medium mb-1">{t("chat.completedFunctions")}</div>
                    <ul className="space-y-1">
                      {messageStatus.chatMessageStatus.functionCalls.map((func: string, index: number) => (
                        <li
                          key={index}
                          className="flex items-center space-x-1"
                        >
                          <span className="text-green-500">✓</span>
                          <span>{func}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

              {/* Cancel button */}
              <div className="flex justify-end">
                <button
                  onClick={onCancelSendMessage}
                  className="text-xs text-red-600 hover:text-red-700 hover:underline"
                >
                  {t("chat.cancel")}
                </button>
              </div>

              {/* Partial content */}
              {messageStatus?.chatMessageStatus?.partialContent && (
                <div className="prose prose-sm dark:prose-invert max-w-none opacity-70">
                  {renderMessageContent(
                    messageStatus.chatMessageStatus.partialContent,
                    null,
                    me.isCoach,
                    handleAthleteClick,
                    t,
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export const ChatMessageList = React.memo(ChatMessageListComponent)
